import { Card, Heading, Link, TextStyle } from '@superops/ui-design-system';
import {
  Loader,
  EventEmitter,
  B1,
  AIActivator,
  getTranslatedText,
  EventEmitterWithIndex,
  AI_AGENTS_MAP
} from '@nile/components';
import { convertMinutesToHours } from '@nile/mangifera/src/Utils/helpers';
import {
  GET_WORKLOG_ENTRIES,
  useCachedQuery,
  useNoCacheQuery,
  useQuery,
  DELETE_WORKLOG_ENTRY
} from '@nile/graphql';
import { TicketContext } from '@nile/mangifera/src/Contexts';
import React, { useEffect, useContext } from 'react';
import { useMutation } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { isEmpty } from 'lodash';
import AddedWorklogEntry from './AddedWorklogEntry';

function AddedWorklogs(props) {
  const {
    workItem,
    ticket,
    intl,
    project,
    module,
    refetch: refetchParent,
    getItemsToInvoice,
    taskId,
    removeTaskBorder,
    hideTask,
    editTippyZIndex,
    hideAI
  } = props;
  const flags = useFlags();
  const context = useContext(TicketContext);
  const getCalculatedPrice = (quantity, price) => {
    let amount = '0';
    if (quantity && price) {
      amount = quantity * price;
      if (typeof amount === 'number') {
        amount = amount.toString();
      }
    }
    return amount;
  };
  const getConditions = () => {
    const operands = [
      {
        attribute: 'workItem.workId',
        operator: 'is',
        value: workItem?.workId
      },
      {
        attribute: 'serviceCatalogItem.serviceTypeItem.offeringType',
        operator: 'is',
        value: 'HOURS'
      }
    ];
    if (props?.taskId) {
      operands.push({
        attribute: 'task.taskId',
        operator: 'is',
        value: taskId
      });
    }
    return {
      joinOperator: 'AND',
      operands: operands
    };
  };
  const { loading, data, error, refetch } = useCachedQuery(
    GET_WORKLOG_ENTRIES,
    {
      variables: {
        worklogInput: {
          module: module,
          listInfo: {
            sort: {
              attribute: 'billDateTime',
              order: 'ASC'
            },
            condition: getConditions()
          }
        }
      },
      onCompleted(res) {
        if (res?.fetchWorklogEntries?.entries) {
          const entries =
            res?.fetchWorklogEntries?.entries.filter(
              (el) =>
                el?.status !== 'INVOICED' &&
                el?.status !== 'IGNORED' &&
                el?.status !== 'UNDER_CONTRACT' &&
                el?.billable
            ) || null;

          if (!taskId) {
            // We need not set items to invoice in task
            // it'll be handled in ticket details or project details
            if (module === 'TICKET') {
              if (getItemsToInvoice) {
                getItemsToInvoice('WORKLOGS', entries);
              }
            } else if (context?.getItemsToInvoice) {
              // TODO: Use hook instead of context
              context.getItemsToInvoice('WORKLOGS', entries);
            }
          }
        }
      }
    }
  );

  const refetchWorklogs = () => {
    refetch();
    if (refetchParent) {
      refetchParent();
    }
  };
  const [
    deleteWorklogEntry,
    { loading: deleteLoading, error: deleteError }
  ] = useMutation(DELETE_WORKLOG_ENTRY, {
    onCompleted() {
      refetchWorklogs();
    }
  });
  const deleteWorklog = (id) => {
    deleteWorklogEntry({
      variables: {
        deleteWorklogInput: {
          itemId: id
        }
      }
    });
  };
  useEffect(() => {
    const index = EventEmitterWithIndex.subscribe(
      'refetchWorklogs',
      refetchWorklogs
    );
    return function cleanup() {
      EventEmitterWithIndex.unsubscribe('refetchWorklogs', index);
    };
  }, []);

  if (loading) {
    return (
      <div className="full-width">
        <Card>
          <Loader />
        </Card>
      </div>
    );
  }
  // if (!data?.fetchWorklogEntries?.entries?.length) {
  //   return '';
  // }
  const worklogs = data?.fetchWorklogEntries?.entries || [];

  let totalTimeSpent = 0;
  let roundedTimeSpent = 0;
  worklogs.forEach((el) => {
    totalTimeSpent += Number(el.qty);
    roundedTimeSpent += Number(el.revisedQuantity);
  });
  // if (workItem) {
  //   totalTimeSpent = Number(workItem?.worklogTimespent || 0) || 0;
  // }

  const getHours = (duration) => {
    const hours = Math.floor(duration / 60);
    return hours;
  };
  const getMinutes = (duration) => {
    const minutes = Math.round(duration % 60);
    return minutes;
  };

  const formatTime = (time) => {
    const timeInMinutes = time * 60;
    return `${getHours(timeInMinutes)}h ${getMinutes(timeInMinutes)}m`;
  };

  const onHeadingClick = () => {
    if (
      props?.mode === 'viewOnly' &&
      !isEmpty(worklogs) &&
      props?.onViewModeClick
    ) {
      props?.onViewModeClick();
    }
  };

  return (
    <div className="full-width">
      <Card>
        <div className="p20 flex col g10">
          <div className="flex fx-sb">
            <div>
              <Heading element="h3">
                <TextStyle weight="SemiBold">
                  <div
                    className={`flex center g10 wrap ${
                      props?.mode === 'viewOnly' && !isEmpty(worklogs)
                        ? 'linkText'
                        : ''
                    }`}
                    onClick={onHeadingClick}
                  >
                    <div>
                      <FormattedMessage id="ticket.details.worklog.totalTimeSpent" />
                    </div>
                    <div>{formatTime(roundedTimeSpent)}</div>
                  </div>
                  <TextStyle variation="subdued">
                    <span className="flex fs10 center g10 wrap">
                      <FormattedMessage id="attribute.worklog.recordedTime" />:
                      {/* <div>{convertMinutesToHours(totalTimeSpent * 60)}</div> */}
                      {formatTime(totalTimeSpent)}
                    </span>
                  </TextStyle>
                  {/* <div>{convertMinutesToHours(totalTimeSpent * 60)}</div> */}
                </TextStyle>
              </Heading>
            </div>

            {module === 'TICKET' && !hideAI && (
              <div className="flex">
                <AIActivator
                  enabledTooltip={getTranslatedText(
                    intl,
                    'msp.ai.feature.worklog.summary'
                  )}
                  featureName={AI_AGENTS_MAP.WORKLOG_SUMMARIZATION}
                  icon="AIWand"
                  disabledTooltip={getTranslatedText(
                    intl,
                    'msp.ai.ticket.worklog.summary.disabled.tooltip.message'
                  )}
                  mixpanelEventOnClick={{
                    event:
                      'Clicked on AI worklog summarise from ticket insights',
                    data: {
                      ticketId: ticket?.ticketId
                    }
                  }}
                />
              </div>
            )}
          </div>

          {props?.mode !== 'viewOnly' && (
            <div className="flex col g20 pl10">
              {worklogs.map((log) => {
                return (
                  <span key={log?.itemId}>
                    <AddedWorklogEntry
                      deleteWorklog={deleteWorklog}
                      log={log}
                      ticket={ticket}
                      workItem={workItem}
                      project={project}
                      module={module}
                      removeTaskBorder={removeTaskBorder}
                      hideTask={hideTask}
                      editTippyZIndex={editTippyZIndex}
                    />
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

export default injectIntl(AddedWorklogs);
